import { formatDate } from '@/shared/lib/format';
import type { PointLog } from '@/shared/types';

export type MovementProgram = 'cashback' | 'points' | 'stamps' | 'balance';

export interface MovementValidityConfig {
  hasExpire?: boolean;
  hasExpireStamps?: boolean;
  expireDays?: number;
}

function parseExpireDays(expireDays?: number): number {
  const days = Number(expireDays ?? 0);
  return Number.isFinite(days) && days > 0 ? days : 0;
}

export function expiryEnabledForProgram(
  passbook: MovementValidityConfig,
  program: MovementProgram,
): boolean {
  if (program === 'stamps') {
    return Boolean(passbook.hasExpireStamps ?? passbook.hasExpire);
  }
  return Boolean(passbook.hasExpire);
}

export function isCreditingMovement(log: PointLog): boolean {
  if (log.value <= 0) return false;
  const action = log.action ?? 'addPoints';
  if (action === 'expirePoints' || action === 'cancel' || action === 'resgate') return false;
  return action === 'addPoints' || action === 'input';
}

/** Saldo desta transação ainda sujeito a expiração (`expire_points` da API). */
export function hasRemainingExpireBalance(expirePoints?: number): boolean {
  if (expirePoints == null) return true;
  return expirePoints > 0.001;
}

/**
 * Data de validade da movimentação.
 * Prioridade: `expirationDate` da API → `updated + expireDays` (expirePointsNovo).
 */
export function computeMovementValidUntil(
  log: PointLog,
  passbook: MovementValidityConfig,
  program: MovementProgram,
): Date | undefined {
  if (log.validUntil) {
    const fromApi = new Date(log.validUntil);
    if (!Number.isNaN(fromApi.getTime())) return fromApi;
  }

  if (!expiryEnabledForProgram(passbook, program)) return undefined;
  if (!isCreditingMovement(log)) return undefined;
  if (!hasRemainingExpireBalance(log.expirePoints)) return undefined;

  const days = parseExpireDays(passbook.expireDays);
  if (days <= 0) return undefined;

  const base = new Date(log.date);
  if (Number.isNaN(base.getTime())) return undefined;

  const until = new Date(base);
  until.setDate(until.getDate() + days);
  until.setHours(0, 0, 0, 0);
  return until;
}

/** Mesmo texto do `CouponModal` no fixe-app (`*Válido até DD/MM/AAAA`). */
export function formatMovementValidUntilCaption(date: Date): string {
  return `*Válido até ${formatDate(date)}`;
}

export function formatMovementValidUntilShort(date: Date): string {
  return `Válido até ${formatDate(date)}`;
}
